import React, { useEffect, useState, useRef } from 'react';
import { useRecoilState } from 'recoil';
import { RadioGroup, FormControlLabel, Radio, InputLabel, FormControl, Grid, Container, TextField } from '@mui/material';
import { viewedReportDataState } from '../../../../recoil/atoms';
import axios from 'axios';
import configParam from '../../../../config';

// ─── Multi-User Typeahead Component (Tailwind styled) ────────────────────────
const MultiUserSelect = ({ id, value, onChange, userOptions, placeholder }) => {
  const [inputVal, setInputVal] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [focused, setFocused] = useState(false);
  const containerRef = useRef(null);

  const selectedNames = value
    ? value.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  const filtered = userOptions.filter(
    (u) =>
      !selectedNames.includes(u.name) &&
      u.name.toLowerCase().includes(inputVal.toLowerCase())
  );

  const addName = (name) => {
    const trimmed = name.trim();
    if (!trimmed || selectedNames.includes(trimmed)) return;
    onChange([...selectedNames, trimmed].join(', '));
    setInputVal('');
    setShowDropdown(false);
  };

  const removeName = (name) => {
    onChange(selectedNames.filter((n) => n !== name).join(', '));
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && inputVal.trim()) {
      e.preventDefault();
      addName(inputVal);
    }
    if (e.key === 'Backspace' && !inputVal && selectedNames.length > 0) {
      removeName(selectedNames[selectedNames.length - 1]);
    }
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {/* Tag input box — same border style as other MUI inputs */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          alignItems: 'center',
          minHeight: '40px',
          padding: '6px 10px',
          border: focused ? '1px solid #1976d2' : '1px solid rgba(0,0,0,0.23)',
          borderRadius: '4px',
          backgroundColor: '#fff',
          cursor: 'text',
          boxShadow: focused ? '0 0 0 1px #1976d2' : 'none',
          transition: 'border-color 0.15s, box-shadow 0.15s',
        }}
        onClick={() => {
          setFocused(true);
          setShowDropdown(true);
          document.getElementById(`${id}-input`)?.focus();
        }}
      >
        {/* Chips — larger, more readable */}
        {selectedNames.map((name) => (
          <span
            key={name}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              backgroundColor: '#1565c0',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 500,
              padding: '4px 10px',
              borderRadius: '20px',
              lineHeight: 1.4,
            }}
          >
            {name}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeName(name); }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.25)',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                lineHeight: 1,
                padding: 0,
              }}
              title={`Remove ${name}`}
            >
              ×
            </button>
          </span>
        ))}

        {/* Text input */}
        <input
          id={`${id}-input`}
          type="text"
          value={inputVal}
          onChange={(e) => { setInputVal(e.target.value); setShowDropdown(true); }}
          onFocus={() => { setFocused(true); setShowDropdown(true); }}
          onKeyDown={handleKeyDown}
          placeholder={selectedNames.length === 0 ? placeholder : 'Add more...'}
          autoComplete="off"
          style={{
            flex: 1,
            minWidth: '130px',
            fontSize: '14px',
            color: '#333',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            padding: '2px 0',
          }}
        />
      </div>

      {/* Dropdown */}
      {showDropdown && (inputVal || filtered.length > 0) && (
        <div
          style={{
            position: 'absolute',
            zIndex: 1300,
            left: 0,
            right: 0,
            marginTop: '4px',
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          {filtered.length > 0 ? (
            filtered.map((u) => (
              <div
                key={u.created_by || u.name}
                onMouseDown={(e) => { e.preventDefault(); addName(u.name); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '9px 14px',
                  fontSize: '14px',
                  color: '#333',
                  cursor: 'pointer',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e3f2fd'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: '#bbdefb',
                    color: '#1565c0',
                    fontWeight: 700,
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {u.name.charAt(0).toUpperCase()}
                </span>
                {u.name}
              </div>
            ))
          ) : inputVal ? (
            <div
              onMouseDown={(e) => { e.preventDefault(); addName(inputVal); }}
              style={{ padding: '9px 14px', fontSize: '14px', color: '#888', cursor: 'pointer', fontStyle: 'italic' }}
            >
              Add &ldquo;{inputVal}&rdquo;
            </div>
          ) : null}
        </div>
      )}

      {/* Hint text */}
      <p style={{ fontSize: '11px', color: '#999', marginTop: '3px', marginLeft: '2px' }}>
        {selectedNames.length > 0
          ? `${selectedNames.length} selected · Press Enter or , to add more`
          : 'Press Enter or comma to add multiple names'}
      </p>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const AreaDes = ({ formData, setFormData }) => {
  const { from, to, copy, date, reference, confidentiality } = formData;
  const [viewedReportData, setViewedReportData] = useRecoilState(viewedReportDataState);
  const [userOptions, setUserOptions] = useState([]);
  const access_token = localStorage.getItem('accesstoken');
  const ApiURL = configParam.API_URL;

  useEffect(() => {
    axios
      .get(ApiURL + '/show_reports_user', {
        headers: { Authorization: `Bearer ${access_token}` },
      })
      .then((response) => setUserOptions(response.data || []))
      .catch(() => setUserOptions([]));
  }, []);

  useEffect(() => {
    if (viewedReportData.date !== date)
      setFormData({ ...formData, date: viewedReportData.date ? viewedReportData.date.split('T')[0] : '' });
    if (viewedReportData.reference !== reference)
      setFormData({ ...formData, reference: viewedReportData.reference });
    if (viewedReportData.confidentiality !== confidentiality)
      setFormData({ ...formData, confidentiality: viewedReportData.confidentiality });
    if (viewedReportData.from !== from)
      setFormData({ ...formData, from: viewedReportData.from });
    if (viewedReportData.to !== to)
      setFormData({ ...formData, to: viewedReportData.to });
    if (viewedReportData.copy !== copy)
      setFormData({ ...formData, copy: viewedReportData.copy });
  }, [
    viewedReportData.from, viewedReportData.to, viewedReportData.copy,
    viewedReportData.date, viewedReportData.reference, viewedReportData.confidentiality,
    from, to, copy, date, reference, confidentiality,
  ]);

  const handleFieldChange = (field, val) => {
    setViewedReportData({ ...viewedReportData, [field]: val });
    setFormData({ ...formData, [field]: val });
  };

  const handleDateChange = (event) => handleFieldChange('date', event.target.value);
  const handleReferenceChange = (event) => handleFieldChange('reference', event.target.value);
  const handleConfidentialityChange = (event) => handleFieldChange('confidentiality', event.target.value);

  return (
    <div>
      <Container maxWidth="lg" style={{ padding: '0 16px' }}>
        <form>
          <Grid container spacing={2}>

            {/* FROM */}
            <Grid item xs={12}>
              <InputLabel style={{ color: 'black', textAlign: 'left', marginTop: '16px' }}>From</InputLabel>
              <FormControl fullWidth>
                <MultiUserSelect
                  id="from"
                  value={from}
                  onChange={(val) => handleFieldChange('from', val)}
                  userOptions={userOptions}
                  placeholder="Type to search or add names..."
                />
              </FormControl>
            </Grid>

            {/* TO */}
            <Grid item xs={12}>
              <InputLabel style={{ color: 'black', textAlign: 'left' }}>To</InputLabel>
              <FormControl fullWidth>
                <MultiUserSelect
                  id="to"
                  value={to}
                  onChange={(val) => handleFieldChange('to', val)}
                  userOptions={userOptions}
                  placeholder="Type to search or add names..."
                />
              </FormControl>
            </Grid>

            {/* COPY */}
            <Grid item xs={12}>
              <InputLabel style={{ color: 'black', textAlign: 'left' }}>Copy</InputLabel>
              <FormControl fullWidth>
                <MultiUserSelect
                  id="copy"
                  value={copy}
                  onChange={(val) => handleFieldChange('copy', val)}
                  userOptions={userOptions}
                  placeholder="Type to search or add names..."
                />
              </FormControl>
            </Grid>

            {/* DATE */}
            <Grid item xs={12} sm={6}>
              <InputLabel style={{ color: 'black', textAlign: 'left' }}>Date</InputLabel>
              <FormControl fullWidth>
                <TextField id="date" type="date" size="small" className="input-nis" value={date} onChange={handleDateChange} />
              </FormControl>
            </Grid>

            {/* REFERENCE */}
            <Grid item xs={12} sm={6}>
              <InputLabel style={{ color: 'black', textAlign: 'left' }}>Reference</InputLabel>
              <FormControl fullWidth>
                <TextField id="reference" type="text" size="small" className="input-nis" value={reference} onChange={handleReferenceChange} disabled={true} />
              </FormControl>
            </Grid>

            {/* CONFIDENTIALITY */}
            <Grid item xs={12}>
              <InputLabel style={{ color: 'black', textAlign: 'left' }}>Confidentiality</InputLabel>
              <FormControl fullWidth>
                <RadioGroup value={confidentiality} onChange={handleConfidentialityChange} row>
                  <FormControlLabel value="1" control={<Radio />} label="1" style={{ marginRight: '16px' }} />
                  <FormControlLabel value="2" control={<Radio />} label="2" style={{ marginRight: '16px' }} />
                  <FormControlLabel value="3" control={<Radio />} label="3" style={{ marginRight: '16px' }} />
                  <FormControlLabel value="4" control={<Radio />} label="4" style={{ marginRight: '16px' }} />
                </RadioGroup>
              </FormControl>
            </Grid>

          </Grid>
        </form>
      </Container>
    </div>
  );
};

export default AreaDes;
